import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { _ as _object_without_properties } from "@swc/helpers/_/_object_without_properties";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment, useMemo, useState } from 'react';
import cn from 'classnames';
import { compact } from 'lodash-es';
import { Typography, Checkbox, ConfirmationModal } from 'seagull';
import { useStoreActions } from '~/contexts';
import { useEventReporter } from '~/event-reporter';
import { useApproveAmendments } from '~/hooks/useApproveAmendments';
import { useLocaleKeys } from '~/i18n';
import { Intent } from '~/infra/access-control';
import { approvalMethodNoticePeriodModel, relationshipModel, serviceModel, timeModel } from '~/models';
import styles from './ApproveAllModal.module.scss';
const pluralize = function pluralize(text, count) {
    let suffix = arguments.length > 2 && arguments[2] !== void 0 ? arguments[2] : 's';
    return count > 1 ? "".concat(text).concat(suffix) : text;
};
export default function ApproveAllModal(param) {
    let { agreement, amendments, invoices, charges, dismissModal } = param;
    const { t: texts } = useLocaleKeys('pages.agreementMinisite');
    const { t: commonTexts } = useLocaleKeys('common');
    const { reportButtonClick } = useEventReporter();
    const { api, logBI } = useStoreActions();
    const [showError, setShowError] = useState(false);
    const [approveInProgress, setApproveInProgress] = useState(false);
    const approveAmendments = useApproveAmendments();
    const firstAutoApprovalTime = useMemo(()=>{
        var _relationshipModel_updatedNetTerms_approvalMethod_autoApprovalDate, _relationshipModel_updatedNetTerms;
        if (!approvalMethodNoticePeriodModel.isAutoApproval(agreement.amendmentsApprovalMethod)) return -1;
        const approvalApproveDates = compact(agreement.services.map((service)=>serviceModel.autoApprovalDate(service)));
        const netTermsApproveDate = (_relationshipModel_updatedNetTerms = relationshipModel.updatedNetTerms(agreement)) === null || _relationshipModel_updatedNetTerms === void 0 ? void 0 : (_relationshipModel_updatedNetTerms_approvalMethod_autoApprovalDate = _relationshipModel_updatedNetTerms.approvalMethod().autoApprovalDate()) === null || _relationshipModel_updatedNetTerms_approvalMethod_autoApprovalDate === void 0 ? void 0 : _relationshipModel_updatedNetTerms_approvalMethod_autoApprovalDate.getTime();
        if (netTermsApproveDate !== undefined) {
            approvalApproveDates.push(netTermsApproveDate);
        }
        return approvalApproveDates.length > 0 ? Math.min(...approvalApproveDates) : -1;
    }, [
        agreement
    ]);
    const [requests, setRequests] = useState(new Map([
        {
            request: ()=>api.relationship.approveAllInvoices(agreement.id),
            label: 'issued invoice',
            count: invoices,
            name: 'invoices',
            enabled: false
        },
        {
            request: ()=>approveAmendments(_object_spread_props(_object_spread({}, agreement), {
                    hideSuccessToast: true,
                    throwOnError: true
                })),
            enabled: false,
            label: 'agreement amendment',
            count: amendments,
            name: 'amendments'
        },
        {
            request: ()=>api.charge.approveAll(agreement.id),
            label: 'awaiting charge',
            count: charges,
            name: 'payments',
            enabled: false
        }
    ].map((_0)=>{
        let { name } = _0, req = _object_without_properties(_0, [
            "name"
        ]);
        return [
            name,
            req
        ];
    })));
    return /*#__PURE__*/ _jsx(ConfirmationModal, {
        bi: {
            primaryButton: {
                name: 'approve_all_confirm'
            },
            secondaryButton: {
                name: 'approve_all_cancel'
            }
        },
        as: "form",
        title: texts.approveAllTitle(),
        className: styles.approveAllModalRoot,
        content: /*#__PURE__*/ _jsxs("div", {
            className: styles.modalContent,
            children: [
                /*#__PURE__*/ _jsx(Typography, {
                    as: "p",
                    children: texts.requestsAwaitingApproval()
                }),
                /*#__PURE__*/ _jsx("div", {
                    children: [
                        ...requests
                    ].filter((param)=>{
                        let [, { count }] = param;
                        return count > 0;
                    }).map((param)=>{
                        let [name, { label, count }] = param;
                        var _ref;
                        var _requests_get;
                        return /*#__PURE__*/ _jsxs(Fragment, {
                            children: [
                                /*#__PURE__*/ _jsx(Checkbox, {
                                    name: name,
                                    testId: "approve-all-modal-checkbox-".concat(name),
                                    checked: (_ref = (_requests_get = requests.get(name)) === null || _requests_get === void 0 ? void 0 : _requests_get.enabled) !== null && _ref !== void 0 ? _ref : false,
                                    className: styles.checkbox,
                                    onChange: (e)=>{
                                        const currentValue = requests.get(name);
                                        if (currentValue) {
                                            requests.set(name, _object_spread_props(_object_spread({}, currentValue), {
                                                enabled: e.currentTarget.checked
                                            }));
                                            setRequests(new Map(requests));
                                        }
                                    },
                                    bi: {
                                        name: 'approve_all_request_toggle',
                                        data: {
                                            request_name: name
                                        }
                                    },
                                    children: /*#__PURE__*/ _jsxs(Typography, {
                                        children: [
                                            /*#__PURE__*/ _jsx("strong", {
                                                children: count
                                            }),
                                            " ",
                                            pluralize(label, count)
                                        ]
                                    })
                                }),
                                approvalMethodNoticePeriodModel.isAutoApproval(agreement.amendmentsApprovalMethod) && amendments > 0 && firstAutoApprovalTime >= 0 && /*#__PURE__*/ _jsx(Typography, {
                                    variant: "paragraph2",
                                    skin: "secondary",
                                    weight: "light",
                                    testId: "approve-all-modal-checkbox-".concat(name, "-description"),
                                    className: styles.checkboxDescription,
                                    children: name === 'amendments' ? "First amendment will be automatically accepted ".concat(timeModel.formatDaysUntil(firstAutoApprovalTime), " (").concat(timeModel.formatDate(firstAutoApprovalTime), ") unless declined.") : "Require".concat(count === 1 ? 's' : '', " approval in order to be paid.")
                                })
                            ]
                        }, name);
                    })
                }),
                /*#__PURE__*/ _jsx(Typography, {
                    className: cn(styles.error, {
                        [styles.errorShown]: showError
                    }),
                    testId: "approve-all-modal-error-label",
                    skin: "error",
                    as: "p",
                    children: "Please select at least one option."
                })
            ]
        }),
        primaryButtonLabel: commonTexts.buttons.approve(),
        primaryButtonDisabled: approveInProgress,
        primaryButtonIntent: Intent.ApproveAllAmendments,
        onPrimaryButtonClick: ()=>{},
        secondaryButtonLabel: commonTexts.buttons.cancel(),
        onSecondaryButtonClick: ()=>dismissModal({
                status: 'canceled'
            }),
        onSubmit: async (e)=>{
            e.preventDefault();
            const requestsToPerform = [
                ...requests
            ].map((param)=>{
                let [, r] = param;
                return r;
            }).filter((param)=>{
                let { enabled } = param;
                return enabled;
            });
            if (requestsToPerform.length === 0) {
                setShowError(true);
                return;
            } else {
                setShowError(false);
            }
            setApproveInProgress(true);
            try {
                for(let index = 0; index < requestsToPerform.length; index++){
                    await requestsToPerform[index].request();
                }
                reportButtonClick('billing-hub-client-approved-amended-agreement-success');
                logBI(10588, {
                    success: true
                });
                dismissModal({
                    status: 'success'
                });
            } catch (e) {
                reportButtonClick('billing-hub-client-approved-amended-agreement-failure');
                logBI(10588, {
                    success: false
                });
                dismissModal({
                    status: 'error'
                });
            } finally{
                setApproveInProgress(false);
            }
        }
    });
}
